import { CommonModule } from "@angular/common";
import {
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from "@angular/core";
import { FormsModule } from "@angular/forms";
import { Router } from "@angular/router";
import { TranslateModule, TranslateService } from "@ngx-translate/core";
import { KeycloakService } from "keycloak-angular";
import { Subscription } from "rxjs";
import { v4 as uuidv4 } from "uuid";
import { SidebarIconComponent } from "../../../layout/sidebar/sidebar-icon/sidebar-icon.component";
import { AuthService } from "../../../services/auth/auth.service";
import { CompanyApiService } from "../../../services/generated/waba-api/services/company-api.service";
import { GlobalSearchService, RecentSearchEntry } from "../../../services/global-search/global-search.service";
import { MenuSections } from "../../../shared/utils/menu-tree";
import { Role } from "../../../shared/utils/role.model";
import { APP_PATHS } from "../../../shared/utils/app-route-paths";
import { SILENT_HTTP_CONTEXT } from "../../../shared/utils/silent-http.util";
import { MenuTreeType } from "../../../types/menu-tree.type";

interface SearchEntry {
  label: string;
  normalized: string;
  normalizedHaystack: string;
  path: string;
  icon: string;
  crumb: string[];
  category: string;
  description: string;
  queryParams?: { [key: string]: string };
}

interface SearchResult extends SearchEntry {
  score: number;
  segments: { text: string; hit: boolean }[];
}

interface ResultGroup {
  category: string;
  items: SearchResult[];
}

/**
 * Stripe-style command palette (Ctrl/Cmd+K) — fuzzy search over every
 * navigable page the user is allowed to see, with recents & quick actions.
 */
@Component({
  selector: "app-global-search",
  standalone: true,
  imports: [CommonModule, FormsModule, TranslateModule, SidebarIconComponent],
  templateUrl: "./global-search.component.html",
  styleUrls: ["./global-search.component.scss"],
})
export class GlobalSearchComponent implements OnInit, OnDestroy {
  isOpen = false;
  query = "";
  groups: ResultGroup[] = [];
  flat: SearchResult[] = [];
  activeIndex = 0;
  recent: RecentSearchEntry[] = [];
  isMac = /Mac|iPhone|iPad/i.test(navigator.platform);

  @Output() openChange = new EventEmitter<boolean>();
  @ViewChild("searchInput") searchInput?: ElementRef<HTMLInputElement>;

  private index: SearchEntry[] = [];
  private sub?: Subscription;
  /** Same company activity used by the sidebar (ECOMMERCE / RESERVATION_HOSTELS). */
  private companyActivity: string | undefined;

  // Known-good creation routes, surfaced as "quick actions" when idle
  private readonly quickActions: Array<{
    labelKey: string;
    descKey: string;
    path: string;
    icon: string;
    authorities?: string[];
    aliases?: string[];
  }> = [
    {
      labelKey: "TEMPLATE.PAGE.CREATE",
      descKey: "GLOBAL_SEARCH.DESC.CREATE_TEMPLATE",
      path: APP_PATHS.workflow.templateRequestNew,
      icon: "clipboard",
      authorities: [Role.MANAGE_TEMPLATE],
      aliases: [
        "create template",
        "new template",
        "add template",
        "creer template",
        "créer template",
        "creer modele",
        "créer modèle",
        "nouveau modele",
        "nouveau modèle",
        "whatsapp template",
      ],
    },
    {
      labelKey: "FLOW_META.PAGE.CREATE",
      descKey: "GLOBAL_SEARCH.DESC.CREATE_FLOW",
      path: APP_PATHS.workflow.formNew,
      icon: "file-text",
      authorities: [Role.MANAGE_FLOW],
      aliases: [
        "create flow",
        "new flow",
        "add flow",
        "creer flow",
        "créer flow",
        "creer flux",
        "créer flux",
        "nouveau flow",
        "nouveau flux",
      ],
    },
    {
      labelKey: "HEADER.SETUP.CAMPAIGN",
      descKey: "GLOBAL_SEARCH.DESC.CREATE_CAMPAIGN",
      path: APP_PATHS.campaigns.new,
      icon: "megaphone",
      authorities: [Role.BROADCAST_ADD],
      aliases: [
        "create campaign",
        "new campaign",
        "add campaign",
        "create broadcast",
        "new broadcast",
        "creer campagne",
        "créer campagne",
        "nouvelle campagne",
        "diffusion",
        "broadcast",
        "campagne",
        "compangne",
      ],
    },
  ];

  constructor(
    private searchService: GlobalSearchService,
    private router: Router,
    private translate: TranslateService,
    private keycloakService: KeycloakService,
    private authService: AuthService,
    private companyApiService: CompanyApiService,
    private host: ElementRef<HTMLElement>,
  ) {}

  ngOnInit(): void {
    this.loadCompanyActivity();
    // External open requests (GlobalSearchService.open()) focus the inline bar
    this.sub = this.searchService.open$.subscribe((open) => {
      if (open && !this.isOpen) {
        this.openPanel();
        this.focusInput(true);
      }
    });
  }

  ngOnDestroy(): void {
    this.sub?.unsubscribe();
  }

  private loadCompanyActivity(): void {
    const companyId = this.authService.getCompanyId();
    if (!companyId) {
      return;
    }
    this.companyApiService
      .getActivity(
        {
          "x-api-requestId": uuidv4(),
          "x-api-canal": "web",
          companyId,
        },
        SILENT_HTTP_CONTEXT,
      )
      .subscribe({
        next: (response: { body?: string }) => {
          this.companyActivity = response.body;
          if (this.isOpen) {
            this.refreshSearchCatalog();
            this.runSearch();
          }
        },
        error: () => {
          this.companyActivity = undefined;
        },
      });
  }

  // ── Global shortcuts / outside click ─────────────────────────────────

  @HostListener("document:keydown", ["$event"])
  onDocumentKeydown(event: KeyboardEvent): void {
    if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === "k") {
      event.preventDefault();
      if (this.isOpen) {
        this.close();
      } else {
        this.openPanel();
        this.focusInput(true);
      }
      return;
    }
    if (!this.isOpen) return;

    if (event.key === "Escape") {
      event.preventDefault();
      this.close();
    } else if (event.key === "ArrowDown") {
      event.preventDefault();
      this.moveActive(1);
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      this.moveActive(-1);
    } else if (event.key === "Enter") {
      event.preventDefault();
      if (this.query.trim()) {
        const active = this.flat[this.activeIndex];
        if (active) this.go(active.path, active);
      } else {
        this.onIdleEnter();
      }
    }
  }

  @HostListener("document:mousedown", ["$event"])
  onDocumentMousedown(event: MouseEvent): void {
    if (
      this.isOpen &&
      !this.host.nativeElement.contains(event.target as Node)
    ) {
      this.close();
    }
  }

  // ── Open / close (inline dropdown, no modal) ─────────────────────────

  openPanel(): void {
    if (this.isOpen) return;
    this.isOpen = true;
    this.openChange.emit(true);
    this.refreshSearchCatalog();
    this.activeIndex = 0;
    this.runSearch();
  }

  /** Rebuild navigable pages and prune stale recents when roles/groups/activity change. */
  private refreshSearchCatalog(): void {
    this.buildIndex();
    const allowedPaths = new Set(this.index.map((entry) => entry.path));
    this.recent = this.searchService
      .getRecent()
      .filter((item) => allowedPaths.has(item.path))
      .map((item) => ({
        ...item,
        description:
          item.description || this.buildDescription(item.crumb || []),
      }));
  }

  close(): void {
    if (!this.isOpen) return;
    this.isOpen = false;
    this.openChange.emit(false);
    this.searchService.close();
    this.searchInput?.nativeElement?.blur();
  }

  private focusInput(selectAll = false): void {
    setTimeout(() => {
      const input = this.searchInput?.nativeElement;
      if (!input) return;
      input.focus();
      if (selectAll) input.select();
    }, 10);
  }

  // ── Index (role/group/activity filtered menu tree — same rules as sidebar) ─

  private buildIndex(): void {
    const entries: SearchEntry[] = [];

    for (const section of MenuSections) {
      if (section.conditional && this.companyActivity !== section.conditional) {
        continue;
      }
      // Mirror sidebar's isSectionVisible: a section-level group requirement gates
      // every item inside it, even when an item also carries its own (broader)
      // authorities — without this, an item with no group of its own (relying on
      // its section's) was reachable through search regardless of group.
      if (section.groups?.length && !this.hasUserGroup(section.groups)) {
        continue;
      }
      const sectionLabel = this.t(section.label);

      for (const menu of section.items) {
        if (!this.isMenuVisibleForSearch(menu)) continue;
        const menuLabel = this.t(menu.label || "");

        if (menu.path) {
          entries.push(
            this.makeEntry(
              menuLabel,
              menu.path,
              menu.icon || "circle-dot",
              [sectionLabel],
              sectionLabel,
              menu.queryParams,
              menu.label || "",
              menu.menu,
            ),
          );
        }

        for (const sub of menu.items || []) {
          if (sub.authorities?.length && !this.isUserInRole(sub.authorities)) {
            continue;
          }
          entries.push(
            this.makeEntry(
              this.t(sub.label),
              sub.path,
              sub.icon || menu.icon || "circle-dot",
              [sectionLabel, menuLabel],
              sectionLabel,
              sub.queryParams,
              sub.label,
              menu.menu,
            ),
          );
        }
      }
    }

    // De-duplicate by path + label + query (rooms/activities share a path)
    const seen = new Set<string>();
    this.index = entries.filter((entry) => {
      const key = `${entry.path}::${entry.label}::${JSON.stringify(entry.queryParams || {})}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }

  /** Mirrors sidebar `isMenuVisible` activity + role + group checks. */
  private isMenuVisibleForSearch(menu: MenuTreeType): boolean {
    if (menu.hidden) return false;
    if (menu.hideForActivity && this.companyActivity === menu.hideForActivity) {
      return false;
    }
    if (
      menu.activityCondition &&
      this.companyActivity !== menu.activityCondition
    ) {
      return false;
    }
    // groups and authorities are independent gates — when both are set, both must
    // pass (see the same fix in sidebar.component.ts's isMenuVisible for the bug
    // this mirrors: picking only one let a broadly-granted role leak an item past
    // a narrower group requirement).
    const groupAllowed = !menu.groups?.length || this.hasUserGroup(menu.groups);
    const roleAllowed = !menu.authorities?.length || this.isUserInRole(menu.authorities);
    return groupAllowed && roleAllowed;
  }

  private makeEntry(
    label: string,
    path: string,
    icon: string,
    crumb: string[],
    category: string,
    queryParams?: { [key: string]: string },
    labelKey = "",
    menuKey?: string,
  ): SearchEntry {
    const description = this.buildDescription(crumb);
    const aliases = this.getSearchAliases(path, labelKey, menuKey);
    return {
      label,
      normalized: this.normalize(label),
      normalizedHaystack: this.normalize(
        [label, description, path, labelKey, menuKey, ...crumb, ...aliases]
          .filter(Boolean)
          .join(" "),
      ),
      path,
      icon,
      crumb,
      category,
      description,
      queryParams,
    };
  }

  private getSearchAliases(
    path: string,
    labelKey: string,
    menuKey?: string,
  ): string[] {
    const aliases = new Set<string>();
    const add = (...values: string[]) =>
      values.forEach((value) => aliases.add(value));
    const key = `${path} ${labelKey} ${menuKey || ""}`.toLowerCase();

    if (key.includes("broadcast")) {
      add(
        "broadcast",
        "diffusion",
        "campaign",
        "campagne",
        "campagnes",
        "compangne",
      );
    }
    if (key.includes("dashboard") || key.includes("analytics")) {
      add(
        "dashboard",
        "tableau de bord",
        "analytics",
        "analytique",
        "statistiques",
        "statistics",
      );
    }
    if (key.includes("contact")) {
      add("contact", "contacts", "client", "customers");
    }
    if (key.includes("team-inbox") || key.includes("inbox")) {
      add(
        "inbox",
        "boite de reception",
        "boîte de réception",
        "messagerie",
        "conversation",
        "conversations",
      );
    }
    if (key.includes("template")) {
      add(
        "template",
        "templates",
        "modele",
        "modèle",
        "modeles",
        "modèles",
        "whatsapp",
      );
    }
    if (key.includes("order")) {
      add("order", "orders", "commande", "commandes");
    }
    if (key.includes("catalog")) {
      add(
        "catalog",
        "catalogue",
        "catalog subscription",
        "souscription catalogue",
      );
    }
    if (key.includes("subscription-meta") || key.includes("meta")) {
      add(
        "meta",
        "whatsapp",
        "meta subscription",
        "souscription meta",
        "connexion meta",
      );
    }
    if (key.includes("payment")) {
      add("payment", "paiement", "paiements");
    }
    if (key.includes("automation")) {
      add(
        "automation",
        "automatisation",
        "automatisations",
        "workflow",
        "flux",
      );
    }
    if (key.includes("ticket")) {
      add("ticket", "tickets", "support");
    }
    if (key.includes("reservation")) {
      add(
        "reservation",
        "reservations",
        "réservation",
        "réservations",
        "chambre",
        "chambres",
      );
    }

    return Array.from(aliases);
  }

  private buildDescription(crumb: string[]): string {
    const path = crumb.filter(Boolean).join(" › ");
    if (path) {
      return this.translate.instant("GLOBAL_SEARCH.DESC_IN", { path });
    }
    return this.t("GLOBAL_SEARCH.DESC_DEFAULT");
  }

  private buildQuickActionSearchEntries(): SearchEntry[] {
    const category = this.t("GLOBAL_SEARCH.QUICK_ACTIONS");
    return this.quickActions
      .filter(
        (action) =>
          !action.authorities?.length || this.isUserInRole(action.authorities),
      )
      .map((action) => {
        const label = this.t(action.labelKey);
        const description = this.t(action.descKey);
        const aliases = [
          ...this.getSearchAliases(action.path, action.labelKey),
          ...(action.aliases || []),
        ];
        return {
          label,
          normalized: this.normalize(label),
          normalizedHaystack: this.normalize(
            [
              label,
              description,
              action.path,
              action.labelKey,
              category,
              ...aliases,
            ]
              .filter(Boolean)
              .join(" "),
          ),
          path: action.path,
          icon: action.icon,
          crumb: [],
          category,
          description,
        };
      });
  }

  private t(key: string): string {
    if (!key) return "";
    const value = this.translate.instant(key);
    return value === key ? key : value;
  }

  private hasUserGroup(groups: string[] | undefined): boolean {
    return this.authService.hasAnyGroup(groups ?? []);
  }

  private isUserInRole(roles: string[]): boolean {
    if (!roles.length) return true;
    if (roles.includes(Role.ADMIN_SOLUTION_UI)) {
      return roles.every((role) => this.keycloakService.isUserInRole(role));
    }
    return roles.some((role) => this.keycloakService.isUserInRole(role));
  }

  // ── Search ───────────────────────────────────────────────────────────

  onQueryChange(): void {
    this.runSearch();
  }

  private runSearch(): void {
    const query = this.normalize(this.query.trim());
    if (!query) {
      this.groups = [];
      this.flat = [];
      this.activeIndex = 0;
      return;
    }

    const actionEntries = this.buildQuickActionSearchEntries();
    const known = new Set(
      this.index.map((entry) => `${entry.path}::${entry.label}`),
    );
    const searchableEntries = [
      ...this.index,
      ...actionEntries.filter(
        (entry) => !known.has(`${entry.path}::${entry.label}`),
      ),
    ];

    const results: SearchResult[] = [];
    for (const entry of searchableEntries) {
      const labelMatch = this.fuzzyMatch(query, entry.normalized);
      const descMatch =
        !labelMatch &&
        this.fuzzyMatch(query, this.normalize(entry.description));
      const haystackMatch =
        !labelMatch && !descMatch
          ? this.fuzzyMatch(query, entry.normalizedHaystack)
          : null;
      const match = labelMatch || descMatch || haystackMatch;
      if (!match) continue;
      results.push({
        ...entry,
        score: match.score + (labelMatch ? 4 : haystackMatch ? 1 : 0),
        segments: labelMatch
          ? this.toSegments(entry.label, labelMatch.indices)
          : [{ text: entry.label, hit: false }],
      });
    }
    results.sort((a, b) => b.score - a.score);

    // Group by category, keep category order by best result
    const byCategory = new Map<string, SearchResult[]>();
    for (const result of results.slice(0, 24)) {
      const list = byCategory.get(result.category) || [];
      if (list.length < 6) list.push(result);
      byCategory.set(result.category, list);
    }
    this.groups = Array.from(byCategory.entries()).map(([category, items]) => ({
      category,
      items,
    }));
    this.flat = this.groups.flatMap((group) => group.items);
    this.activeIndex = 0;
  }

  /** Subsequence fuzzy match with word-start + adjacency bonuses. */
  private fuzzyMatch(
    query: string,
    target: string,
  ): { score: number; indices: number[] } | null {
    let score = 0;
    let targetIdx = 0;
    let previousHit = -2;
    const indices: number[] = [];

    for (const char of query) {
      if (char === " ") {
        previousHit = -2;
        continue;
      }
      let found = -1;
      while (targetIdx < target.length) {
        if (target[targetIdx] === char) {
          found = targetIdx;
          break;
        }
        targetIdx++;
      }
      if (found === -1) return null;

      score += 1;
      if (found === 0 || target[found - 1] === " ") score += 8; // word start
      if (found === previousHit + 1) score += 4; // consecutive
      indices.push(found);
      previousHit = found;
      targetIdx = found + 1;
    }

    // Prefer shorter labels & matches near the start
    score += Math.max(0, 12 - (indices[0] ?? 0));
    score -= Math.min(6, Math.floor(target.length / 12));
    return { score, indices };
  }

  private normalize(value: string): string {
    return value.toLowerCase().normalize("NFD").replace(/[̀-ͯ]/g, "");
  }

  /** Split the label into hit/miss segments for <mark>-style highlighting. */
  private toSegments(
    label: string,
    indices: number[],
  ): { text: string; hit: boolean }[] {
    const hitSet = new Set(indices);
    const segments: { text: string; hit: boolean }[] = [];
    let current = "";
    let currentHit: boolean | null = null;

    for (let i = 0; i < label.length; i++) {
      const hit = hitSet.has(i);
      if (currentHit === null || hit === currentHit) {
        current += label[i];
        currentHit = hit;
      } else {
        segments.push({ text: current, hit: currentHit });
        current = label[i];
        currentHit = hit;
      }
    }
    if (current) segments.push({ text: current, hit: !!currentHit });
    return segments;
  }

  // ── Navigation ───────────────────────────────────────────────────────

  moveActive(delta: number): void {
    const total = this.flat.length || this.idleCount;
    if (!total) return;
    this.activeIndex = (this.activeIndex + delta + total) % total;
    setTimeout(() => {
      document
        .getElementById("gsp-option-" + this.activeIndex)
        ?.scrollIntoView({ block: "nearest" });
    });
  }

  /** Idle mode (no query): recents, default pages, then quick actions share the index space. */
  get idleCount(): number {
    return (
      this.recent.length + this.defaultPageEntries.length + this.quickActionEntries.length
    );
  }

  /** Top navigable pages for the current user (role/group/activity filtered). */
  get defaultPageEntries(): RecentSearchEntry[] {
    const seenCategories = new Set<string>();
    const entries: RecentSearchEntry[] = [];

    for (const entry of this.index) {
      if (entries.length >= 12) {
        break;
      }
      if (seenCategories.has(entry.category) && entries.length >= 6) {
        continue;
      }
      seenCategories.add(entry.category);
      entries.push({
        label: entry.label,
        path: entry.path,
        icon: entry.icon,
        crumb: entry.crumb,
        description: entry.description,
        queryParams: entry.queryParams,
      });
    }

    if (entries.length < 12) {
      for (const entry of this.index) {
        if (entries.length >= 12) {
          break;
        }
        if (entries.some((item) => item.path === entry.path && item.label === entry.label)) {
          continue;
        }
        entries.push({
          label: entry.label,
          path: entry.path,
          icon: entry.icon,
          crumb: entry.crumb,
          description: entry.description,
          queryParams: entry.queryParams,
        });
      }
    }

    return entries;
  }

  get quickActionEntries(): RecentSearchEntry[] {
    return this.quickActions
      .filter(
        (action) =>
          !action.authorities?.length || this.isUserInRole(action.authorities),
      )
      .map((action) => ({
        label: this.t(action.labelKey),
        path: action.path,
        icon: action.icon,
        crumb: [],
        description: this.t(action.descKey),
      }));
  }

  onIdleEnter(): void {
    const idle = [
      ...this.recent,
      ...this.defaultPageEntries,
      ...this.quickActionEntries,
    ];
    const active = idle[this.activeIndex];
    if (active) this.go(active.path, active);
  }

  go(
    path: string,
    entry: {
      label: string;
      path: string;
      icon: string;
      crumb: string[];
      description?: string;
      queryParams?: { [key: string]: string };
    },
  ): void {
    this.searchService.addRecent({
      label: entry.label,
      path: entry.path,
      icon: entry.icon,
      crumb: entry.crumb,
      description: entry.description || this.buildDescription(entry.crumb),
      queryParams: entry.queryParams,
    });
    this.close();
    this.query = "";
    if (entry.queryParams && Object.keys(entry.queryParams).length) {
      this.router.navigate([path], { queryParams: entry.queryParams });
    } else {
      this.router.navigateByUrl(path);
    }
  }

  clearRecent(event: Event): void {
    event.stopPropagation();
    this.searchService.clearRecent();
    this.recent = [];
    this.activeIndex = 0;
  }

  trackByPath(_i: number, item: { path: string; label: string }): string {
    return item.path + item.label;
  }
}
